// Import Dependencies
import PropTypes from "prop-types";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import clsx from "clsx";

// Local Imports
import { setThisClass } from "utils/setThisClass";
import { getLocalDateTime } from "utils/dateUtils";
import { useLocaleContext } from "app/contexts/locale/context";
import { useTimelineContext } from "./context";
import { COLORS } from "constants/app.constant";

// ----------------------------------------------------------------------

dayjs.extend(relativeTime);

const variants = {
  filled: "bg-this dark:bg-this-light",
  outlined: "border-2 border-this dark:border-this-light",
};

const neutralVariant = {
  filled: "bg-gray-300 dark:bg-dark-400",
  outlined: "border-2 border-gray-300 dark:border-dark-400",
};

const TimelineItem = ({
  children,
  title,
  time,
  point,
  color = "neutral",
  variant,
  className,
  classNames = {},
  isPing,
  ...rest
}) => {
  const { locale } = useLocaleContext();

  const ctx = useTimelineContext();

  const mergedVariant = variant || ctx.variant;
  /**
   * Returns an exact readable difference between two dates
   * Example: "18 hours 42 minutes ago" or "in 2 days 4 hours"
   */
  const formatExactTimeDifference = (dateString, locale = "en") => {
    try {
      const target = getLocalDateTime(dateString);
      if (isNaN(target.getTime())) return "Invalid date";

      const now = new Date();
      const diffMs = target - now; // + future, - past
      const absMs = Math.abs(diffMs);

      // Convert milliseconds to parts
      const totalSeconds = Math.floor(absMs / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);
      const totalDays = Math.floor(totalHours / 24);

      const days = totalDays;
      const hours = totalHours % 24;
      const minutes = totalMinutes % 60;
      const seconds = totalSeconds % 60;

      // Build human-readable string
      let parts = [];
      if (days) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
      if (hours) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
      if (minutes) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
      if (!days && !hours && !minutes)
        parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);

      const result = parts.slice(0, 2).join(" "); // show up to 2 units (e.g. "18 hours 42 minutes")

      return diffMs < 0 ? `${result} ago` : `in ${result}`;
    } catch {
      return "Unknown time";
    }
  };


  const formattedTime = formatExactTimeDifference(time, locale);

  const pointNode = (
    <div
      className={clsx(
        "timeline-item-point relative flex shrink-0 items-center justify-center rounded-full",
        color === "neutral"
          ? neutralVariant[mergedVariant]
          : [setThisClass(color), variants[mergedVariant]],
        classNames?.point,
      )}
    >
      {isPing && (
        <span className="inline-flex h-full w-full animate-ping rounded-full bg-inherit opacity-80"></span>
      )}
    </div>
  );

  return (
    <div className={clsx("timeline-item", className, classNames?.root)} {...rest}>
      {point ? point : pointNode}

      <div
        className={`timeline-item-content-wrappper ${classNames?.contentWrapper}`}
      >
        <div className="flex flex-col pb-1.5">
          <h3
            className={clsx(
              "pb-1.5 font-medium leading-none text-gray-600 dark:text-dark-100",
              classNames?.title,
            )}
          >
            {title}
          </h3>
          <span
            className={clsx(
              "text-xs text-gray-400 dark:text-dark-300",
              classNames?.time,
            )}
          >
            {formattedTime}
          </span>
        </div>
        <div className={clsx("timeline-item-content py-1", classNames?.content)}>
          {children}
        </div>
      </div>
    </div>
  );
};

TimelineItem.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  point: PropTypes.node,
  color: PropTypes.oneOf(COLORS),
  variant: PropTypes.oneOf(["filled", "outlined"]),
  className: PropTypes.string,
  classNames: PropTypes.object,
  isPing: PropTypes.bool,
};

export { TimelineItem };
